/**
 * Layer-2 (Zod fetch-boundary) STRUCTURAL GUARDS for the SMS Marketing app.
 *
 * These guards are passed to `useApiQuery`'s `schema:` option (via
 * {@link asResponseGuard}) so the unwrapped, post-transform payload is validated
 * at the network boundary. A failure throws an attributable
 * `ResponseValidationError` carrying the request URL instead of surfacing as a
 * `Cannot read properties of undefined` crash deep in a component render.
 *
 * POSTURE — DELIBERATELY LENIENT STRUCTURAL GUARD (NOT a full contracts schema):
 *   - No element / identity field is asserted.
 *   - `.passthrough()` on every object guard so a guard can NEVER drop a field a
 *     rendering page reads.
 *   - `{ items }` envelopes only assert that, IF present, `items` is an array —
 *     catching the bare un-unwrapped object / null-body / wrong-type drift.
 *
 * These guards CANNOT over-reject a response the UI renders today.
 */

import { z } from "zod";

/**
 * Cast a structural Zod guard to the hook's `ResponseValidator<T>` seam.
 *
 * The hand-written `<T>` (types/sms.ts) owns the STATIC type; this guard owns
 * RUNTIME structure only. The lenient guard's inferred output intentionally
 * differs from `<T>`, so the cast bridges the two layers without weakening the
 * static type.
 */
export function asResponseGuard<T>(schema: { parse(data: unknown): unknown }): {
  parse(data: unknown): T;
} {
  return schema as { parse(data: unknown): T };
}

/** Lenient object guard — asserts a non-null object; preserves every field. */
export const ObjectResponseGuard = z.object({}).passthrough();

/** Lenient array guard — asserts an array payload (catches un-unwrapped object / null body). */
export const ArrayResponseGuard = z.array(z.unknown());

/**
 * Lenient `{ items: [...] }` envelope guard.
 *
 * The SMS list endpoints tolerate BOTH the `{ items, total }` envelope AND a
 * bare top-level array — every consuming hook reads `query.data?.items`, so a
 * bare array degrades to an empty list rather than crashing. The guard accepts
 * either container and preserves `total` / etc.; it rejects only a
 * null/primitive/error-string body — the gross drift this layer exists to catch.
 */
export const ItemsEnvelopeGuard = z.union([
  z.object({ items: z.array(z.unknown()).nullish() }).passthrough(),
  z.array(z.unknown()),
]);

/** Extract the inner `opt_outs` array from the /compliance/opt-outs envelope. */
export const OptOutsEnvelopeGuard = z
  .object({ opt_outs: z.array(z.unknown()).nullish() })
  .passthrough()
  .transform((o) => o.opt_outs ?? []);

/** Extract the inner `records` array from the /compliance/consent/{id}/history envelope. */
export const ConsentHistoryEnvelopeGuard = z
  .object({ records: z.array(z.unknown()).nullish() })
  .passthrough()
  .transform((o) => o.records ?? []);

/** Extract the inner `runs` array from the /automations/runs/history envelope. */
export const RunHistoryEnvelopeGuard = z
  .object({ runs: z.array(z.unknown()).nullish() })
  .passthrough()
  .transform((o) => o.runs ?? []);

/** Extract the inner `daily_volume` array from the /analytics/daily-volume envelope. */
export const DailyVolumeEnvelopeGuard = z
  .object({ daily_volume: z.array(z.unknown()).nullish() })
  .passthrough()
  .transform((o) => o.daily_volume ?? []);
